/**
 * Canonical Synthetic Dataset for TALKTO+ Enterprise Demo
 * Scenario: Northstar Transformation Program
 * Persona: Thomas Nordstrom (Board Director)
 * All IDs are stable and resolve consistently across Boardroom, Work, Intelligence, Chat, Canvas, Worktree, Data, and Enterprise.
 */

export const CANONICAL_PROGRAM = Object.freeze({
  id: 'demo-program-northstar',
  name: 'Northstar Transformation Program',
  code: 'PRG-NORTHSTAR',
  sector: 'Industrial Manufacturing & Operations',
  description: 'Enterprise operational modernization & AI workflow governance program.',
  status: 'ACTIVE',
  lead_sponsor: 'Thomas Nordstrom (Board Director)',
  created_at: '2026-08-01T08:00:00Z',
  updated_at: '2026-08-28T07:30:00Z',
});

export const CANONICAL_RISKS = Object.freeze([
  {
    id: 'demo-risk-delivery-dependency',
    public_ref: 'risk_dependency',
    label: 'Delivery Dependency',
    category: 'Operational',
    severity: 'CRITICAL',
    state: 'EMERGING',
    impact_score: 4.8,
    summary: 'A critical external supply/service vendor dependency requires an evidence-backed mitigation decision before stage progression.',
    mitigation_owner: 'Operations Steering Group',
    status: 'ACTIVE_MITIGATION',
  },
  {
    id: 'demo-risk-strategic-alignment',
    public_ref: 'risk_alignment',
    label: 'Strategic Alignment',
    category: 'Governance',
    severity: 'HIGH',
    state: 'CONFIRMED',
    impact_score: 3.9,
    summary: 'Executive decision threshold requires formal board alignment on transformation scope and capital allocation.',
    mitigation_owner: 'Board Executive Committee',
    status: 'CONFIRMED',
  },
  {
    id: 'demo-risk-evidence-sufficiency',
    public_ref: 'risk_evidence_sufficiency',
    label: 'Evidence Sufficiency',
    category: 'Assurance',
    severity: 'MEDIUM',
    state: 'MONITORED',
    impact_score: 2.7,
    summary: 'Current evidence coverage is at 86%; implementation capacity assumptions require one remaining audit memo.',
    mitigation_owner: 'Assurance Lead',
    status: 'UNDER_REVIEW',
  },
  {
    id: 'demo-risk-operational-capacity',
    public_ref: 'risk_capacity',
    label: 'Operational Capacity',
    category: 'Execution',
    severity: 'MEDIUM',
    state: 'CONFIRMED',
    impact_score: 2.5,
    summary: 'Internal specialized engineering band allocations meet 92% of stage-4 requirements.',
    mitigation_owner: 'Program Delivery Director',
    status: 'CONFIRMED',
  },
]);

export const CANONICAL_SIGNALS = Object.freeze([
  {
    id: 'demo-signal-dependency-mitigation',
    content: 'Critical external supplier committed to stage-3 delivery timeline under revised SLA.',
    risk_category: 'risk_dependency',
    risk_level: 'CRITICAL',
    state: 'EMERGING',
    confidence: 0.94,
    source: 'Vendor Service Level Audit',
    validated_at: '2026-08-28T06:45:00Z',
    cascade_blocking_gates: ['evidence_gate', 'red_team_gate'],
    epistemic_type: 'OBSERVED',
    related_risk_id: 'demo-risk-delivery-dependency',
  },
  {
    id: 'demo-signal-call-fit',
    content: 'Executive decision threshold confirmed into written go/no-go governance criteria.',
    risk_category: 'risk_alignment',
    risk_level: 'HIGH',
    state: 'CONFIRMED',
    confidence: 0.98,
    source: 'Steering Committee Governance Minutes',
    validated_at: '2026-08-28T07:30:00Z',
    cascade_blocking_gates: ['board_alignment'],
    epistemic_type: 'OBSERVED',
    related_risk_id: 'demo-risk-strategic-alignment',
  },
  {
    id: 'demo-signal-partner-commitment',
    content: 'Partner commitment validated for stage-gate progression with zero residual indemnity.',
    risk_category: 'risk_alignment',
    risk_level: 'HIGH',
    state: 'CONFIRMED',
    confidence: 0.91,
    source: 'Partner Engagement Ledger',
    validated_at: '2026-08-27T14:15:00Z',
    cascade_blocking_gates: [],
    epistemic_type: 'OBSERVED',
    related_risk_id: 'demo-risk-strategic-alignment',
  },
  {
    id: 'demo-signal-capacity-gap',
    content: 'Evidence gap remaining in implementation capacity assumptions under heavy load scenario.',
    risk_category: 'risk_evidence_sufficiency',
    risk_level: 'MEDIUM',
    state: 'MONITORED',
    confidence: 0.88,
    source: 'Technical Architecture Assessment',
    validated_at: '2026-08-27T11:00:00Z',
    cascade_blocking_gates: ['capacity_gate'],
    epistemic_type: 'INFERRED',
    related_risk_id: 'demo-risk-evidence-sufficiency',
  },
]);

export const CANONICAL_EVIDENCE = Object.freeze([
  {
    id: 'demo-evi-steering-record',
    title: 'Executive Steering Group Decision Record',
    type: 'GOVERNANCE_MINUTES',
    source_type: 'Steering Committee Minutes',
    status: 'VERIFIED',
    confidence: 0.99,
    source_uri: 'vault://governance/2026-08-steering-record.pdf',
    provenance_hash: '0x8f2a4c91d0e4',
    validated_at: '2026-08-28T07:15:00Z',
    author: 'Corporate Secretary',
    summary: 'Formal steering approval for conditional stage progression subject to vendor mitigation.',
  },
  {
    id: 'demo-evi-call-fit',
    title: 'Northstar Operational Readiness Assessment',
    type: 'READINESS_ASSESSMENT',
    source_type: 'Operations Review',
    status: 'VERIFIED',
    confidence: 0.96,
    source_uri: 'vault://assessments/northstar-readiness-2026.pdf',
    provenance_hash: '0x7c3b9e12f4a8',
    validated_at: '2026-08-28T06:00:00Z',
    author: 'Principal Systems Auditor',
    summary: 'Technical and operational capability baseline confirms 86% readiness across core streams.',
  },
  {
    id: 'demo-evi-partner-memo',
    title: 'Partner Integration & SLA Commitment Memo',
    type: 'COMMERCIAL_CONTRACT',
    source_type: 'Vendor Service Level Audit',
    status: 'VERIFIED',
    confidence: 0.92,
    source_uri: 'vault://contracts/partner-sla-2026.pdf',
    provenance_hash: '0x3d9a1188b2c4',
    validated_at: '2026-08-27T16:30:00Z',
    author: 'Commercial Director',
    summary: 'Binding partner milestone commitments guaranteeing Q4 execution continuity.',
  },
  {
    id: 'demo-evi-delivery-review',
    title: 'Vendor Dependency Mitigation Plan',
    type: 'TECHNICAL_SPEC',
    source_type: 'Architecture Review',
    status: 'IN_REVIEW',
    confidence: 0.89,
    source_uri: 'vault://reviews/vendor-mitigation-plan.pdf',
    provenance_hash: '0x5b2e7740a1d9',
    validated_at: '2026-08-27T10:20:00Z',
    author: 'Lead Enterprise Architect',
    summary: 'Fallback architecture design bypassing single point of failure in data pipeline.',
  },
  {
    id: 'demo-evi-capacity-audit',
    title: 'Engineering Capacity & Allocation Review',
    type: 'RESOURCE_AUDIT',
    source_type: 'Internal Audit',
    status: 'VERIFIED',
    confidence: 0.91,
    source_uri: 'vault://audits/capacity-review-2026.pdf',
    validated_at: '2026-08-26T12:00:00Z',
    author: 'Head of Engineering',
    summary: 'Resource modeling confirms core team availability across the 7-stage workflow.',
  },
]);

export const CANONICAL_DECISIONS = Object.freeze([
  {
    id: 'demo-dec-stage-gate',
    title: 'Conditional Progression to Stage 4',
    status: 'PENDING_APPROVAL',
    stage: 'Stage 6: Human Approval',
    risk_level: 'HIGH',
    recommendation: 'PROCEED_WITH_CONDITIONS',
    conditions: [
      'Evidence coverage maintained above 85%',
      'Vendor SLA mitigation signed by Sept 1',
      'Dual-operator review required for pipeline activation',
    ],
    rationale: 'Strategic alignment confirmed; delivery dependency contained within threshold.',
  },
  {
    id: 'demo-dec-dependency-mitigation',
    title: 'Authorize Secondary Pipeline Failover Agreement',
    status: 'APPROVED',
    stage: 'Stage 4: Decision Framing',
    risk_level: 'CRITICAL',
    recommendation: 'APPROVE',
    conditions: ['Budget variance capped at 4.2%'],
    rationale: 'Eliminates single vendor bottleneck for critical data synchronization.',
  },
  {
    id: 'demo-dec-capacity-evidence',
    title: 'Validate Final Engineering Allocation Audit',
    status: 'READY_FOR_REVIEW',
    stage: 'Stage 5: Red-Team Review',
    risk_level: 'MEDIUM',
    recommendation: 'VERIFY',
    conditions: ['Confirmed by Lead Architect'],
    rationale: 'Ensures operational stability during high-volume cutover.',
  },
]);

export const CANONICAL_WORKFLOW_STAGES = Object.freeze([
  { id: 'stage-1', order: 1, name: 'Evidence Intake', status: 'COMPLETED', progress: 100, tasks_count: 3, owner: 'Assurance Lead' },
  { id: 'stage-2', order: 2, name: 'Signal Validation', status: 'COMPLETED', progress: 100, tasks_count: 4, owner: 'Intelligence Analyst' },
  { id: 'stage-3', order: 3, name: 'Risk Assessment', status: 'COMPLETED', progress: 100, tasks_count: 3, owner: 'Risk Committee' },
  { id: 'stage-4', order: 4, name: 'Decision Framing', status: 'IN_PROGRESS', progress: 75, tasks_count: 2, owner: 'Steering Group' },
  { id: 'stage-5', order: 5, name: 'Red-Team Review', status: 'IN_PROGRESS', progress: 60, tasks_count: 2, owner: 'Independent Red Team' },
  { id: 'stage-6', order: 6, name: 'Human Approval', status: 'PENDING', progress: 20, tasks_count: 1, owner: 'Thomas Nordstrom' },
  { id: 'stage-7', order: 7, name: 'Execution & Monitoring', status: 'PENDING', progress: 0, tasks_count: 3, owner: 'Delivery Office' },
]);

export const CANONICAL_WORK_TASKS = Object.freeze([
  {
    id: 'demo-task-1',
    title: 'Review Vendor Dependency SLA mitigation memo',
    stage: 'Decision Framing',
    assigned_to: 'Thomas Nordstrom',
    role: 'Board Director',
    status: 'ACTION_REQUIRED',
    priority: 'CRITICAL',
    due_date: '2026-08-30',
    linked_evidence_id: 'demo-evi-delivery-review',
    linked_risk_id: 'demo-risk-delivery-dependency',
  },
  {
    id: 'demo-task-2',
    title: 'Sign off Stage 4 to Stage 5 gate review',
    stage: 'Human Approval',
    assigned_to: 'Thomas Nordstrom',
    role: 'Board Director',
    status: 'IN_REVIEW',
    priority: 'HIGH',
    due_date: '2026-08-31',
    linked_decision_id: 'demo-dec-stage-gate',
    linked_risk_id: 'demo-risk-strategic-alignment',
  },
  {
    id: 'demo-task-3',
    title: 'Verify engineering allocation audit report',
    stage: 'Red-Team Review',
    assigned_to: 'Anna Tunnuslause',
    role: 'Program Director',
    status: 'COMPLETED',
    priority: 'MEDIUM',
    due_date: '2026-08-27',
    linked_evidence_id: 'demo-evi-capacity-audit',
    linked_risk_id: 'demo-risk-operational-capacity',
  },
  {
    id: 'demo-task-4',
    title: 'Validate call-fit telemetry thresholds',
    stage: 'Signal Validation',
    assigned_to: 'Anna Virtanen',
    role: 'Executive Sponsor',
    status: 'COMPLETED',
    priority: 'HIGH',
    due_date: '2026-08-26',
    linked_signal_id: 'demo-signal-call-fit',
    linked_risk_id: 'demo-risk-strategic-alignment',
  },
]);

export const CANONICAL_KNOWLEDGE_DOCUMENTS = Object.freeze([
  {
    id: 'demo-doc-charter',
    title: 'Northstar Program Governance Charter',
    category: 'Governance',
    classification: 'OFFICIAL',
    updated_at: '2026-08-20',
    citations: 14,
    excerpt: 'Defines four-eyes approval principles, gate criteria, and sovereign boundaries for program execution.',
  },
  {
    id: 'demo-doc-architecture',
    title: 'Air-Gapped Sovereign Intelligence Architecture',
    category: 'Technical Specification',
    classification: 'OFFICIAL',
    updated_at: '2026-08-25',
    citations: 28,
    excerpt: 'Specifies local model inferencing, zero external WAN egress, and tenant cryptographic separation.',
  },
  {
    id: 'demo-doc-risk-framework',
    title: 'Enterprise Execution Risk & Mitigation Framework',
    category: 'Risk Policy',
    classification: 'OFFICIAL',
    updated_at: '2026-08-15',
    citations: 19,
    excerpt: 'Deterministic classification rules separating observed truth from statistical inferences.',
  },
]);

export const CANONICAL_AGENTS = Object.freeze([
  {
    id: 'agent-execution',
    name: 'Execution Analyst',
    specialty: 'Milestones & Delivery',
    authority: 'GOVERNED_AGENT',
    description: 'Tracks critical path dependencies, blockers, and timeline variances across stages.',
    status: 'ACTIVE',
  },
  {
    id: 'agent-evidence',
    name: 'Evidence Analyst',
    specialty: 'Knowledge & Citations',
    authority: 'GOVERNED_AGENT',
    description: 'Verifies evidentiary sufficiency, citations, and mathematical proof linkages.',
    status: 'ACTIVE',
  },
  {
    id: 'agent-risk',
    name: 'Risk Analyst',
    specialty: 'Dependencies & Gaps',
    authority: 'GOVERNED_AGENT',
    description: 'Monitors causal impact, emerging bottlenecks, and cascade risks across nodes.',
    status: 'ACTIVE',
  },
]);

export const CANONICAL_CANVAS_OBJECTS = Object.freeze([
  {
    id: 'cnv-obj-1',
    type: 'PROGRAM',
    title: 'Northstar Program Root',
    x: 420,
    y: 80,
    status: 'ACTIVE',
    badge: 'Program Root',
  },
  {
    id: 'cnv-obj-2',
    type: 'DECISION',
    title: 'Conditional Stage 4 Progression',
    x: 180,
    y: 220,
    status: 'PENDING_APPROVAL',
    badge: 'Stage 6 Gate',
  },
  {
    id: 'cnv-obj-3',
    type: 'RISK',
    title: 'Delivery Dependency',
    x: 420,
    y: 220,
    status: 'CRITICAL',
    badge: 'Critical Risk',
  },
  {
    id: 'cnv-obj-4',
    type: 'EVIDENCE',
    title: 'Vendor Mitigation Memo',
    x: 660,
    y: 220,
    status: 'VERIFIED',
    badge: 'Evidence Record',
  },
  {
    id: 'cnv-obj-5',
    type: 'SIGNAL',
    title: 'SLA Milestone Commitment',
    x: 420,
    y: 360,
    status: 'OBSERVED',
    badge: 'Validated Signal',
  },
  {
    id: 'cnv-obj-6',
    type: 'ACTION',
    title: 'Secondary Pipeline Failover',
    x: 660,
    y: 360,
    status: 'IN_PROGRESS',
    badge: 'Mitigation Action',
  },
  {
    id: 'cnv-obj-7',
    type: 'HUMAN_REVIEW',
    title: 'Thomas Nordstrom Review',
    x: 180,
    y: 360,
    status: 'ASSIGNED',
    badge: 'Four-Eyes Review',
  },
]);

export const CANONICAL_CANVAS_ACTIVITY = Object.freeze([
  { id: 'act-1', timestamp: '10 mins ago', author: 'Thomas Nordstrom', action: 'Requested executive brief on Delivery Dependency', badge: 'Review' },
  { id: 'act-2', timestamp: '25 mins ago', author: 'Risk Analyst (AI)', action: 'Evaluated causal impact on Q4 timeline: 4.8 severity', badge: 'Analysis' },
  { id: 'act-3', timestamp: '1 hour ago', author: 'Anna Tunnuslause', action: 'Attached Vendor Mitigation Memo to Stage 4 Gate', badge: 'Evidence' },
  { id: 'act-4', timestamp: '2 hours ago', author: 'Evidence Analyst (AI)', action: 'Verified citation integrity across 5 core records', badge: 'Verification' },
  { id: 'act-5', timestamp: '3 hours ago', author: 'Steering Committee', action: 'Approved conditional progression parameters', badge: 'Decision' },
  { id: 'act-6', timestamp: 'Yesterday', author: 'Execution Analyst (AI)', action: 'Generated Northstar milestone progression forecast', badge: 'Milestone' },
]);

export const CANONICAL_EXECUTION_GRAPH = Object.freeze({
  nodes: [
    { id: 'node-root', label: 'Northstar Transformation Program', type: 'PROGRAM', depth: 0, status: 'ACTIVE', x: 500, y: 80 },
    { id: 'node-boardroom', label: 'Boardroom Governance', type: 'DOMAIN', depth: 1, status: 'READY_WITH_CONDITIONS', x: 200, y: 220 },
    { id: 'node-work', label: 'Work Progression', type: 'DOMAIN', depth: 1, status: 'IN_PROGRESS', x: 400, y: 220 },
    { id: 'node-intel', label: 'Execution Intelligence', type: 'DOMAIN', depth: 1, status: 'MONITORED', x: 600, y: 220 },
    { id: 'node-canvas', label: 'Collaboration Canvas', type: 'DOMAIN', depth: 1, status: 'ACTIVE', x: 800, y: 220 },
    { id: 'node-risk-1', label: 'Delivery Dependency', type: 'RISK', depth: 2, status: 'CRITICAL', x: 300, y: 380, risk_score: 4.8 },
    { id: 'node-risk-2', label: 'Strategic Alignment', type: 'RISK', depth: 2, status: 'HIGH', x: 500, y: 380, risk_score: 3.9 },
    { id: 'node-dec-1', label: 'Stage 4 Conditional Approval', type: 'DECISION', depth: 2, status: 'PENDING', x: 200, y: 520 },
    { id: 'node-evi-1', label: 'Vendor Mitigation Memo', type: 'EVIDENCE', depth: 3, status: 'VERIFIED', x: 400, y: 520 },
    { id: 'node-act-1', label: 'Secondary Failover Activation', type: 'ACTION', depth: 3, status: 'IN_PROGRESS', x: 600, y: 520 },
  ],
  edges: [
    { id: 'e-1', source: 'node-root', target: 'node-boardroom', type: 'GOVERNS' },
    { id: 'e-2', source: 'node-root', target: 'node-work', type: 'EXECUTES' },
    { id: 'e-3', source: 'node-root', target: 'node-intel', type: 'ANALYZES' },
    { id: 'e-4', source: 'node-root', target: 'node-canvas', type: 'COLLABORATES' },
    { id: 'e-5', source: 'node-boardroom', target: 'node-risk-2', type: 'MONITORS' },
    { id: 'e-6', source: 'node-work', target: 'node-risk-1', type: 'MITIGATES' },
    { id: 'e-7', source: 'node-risk-1', target: 'node-dec-1', type: 'BLOCKS' },
    { id: 'e-8', source: 'node-dec-1', target: 'node-evi-1', type: 'REQUIRES' },
    { id: 'e-9', source: 'node-risk-1', target: 'node-act-1', type: 'TRIGGERS' },
  ],
  metrics: {
    potential_exposure_eur: 420000,
    hours_at_risk: 180,
    avoidable_work_percentage: 14,
    node_count: 10,
    edge_count: 9,
  },
});
